/**
 * The time zone a business keeps its calendar in: what "today" means for its
 * invoice and due dates, and for every report run "as of today".
 *
 * A BUSINESS WITH NO ZONE RUNS ON UTC. The api resolves an unset zone to UTC
 * (`resolved_time_zone`), so an invoice issued at 9 PM Eastern is dated
 * tomorrow. The api only seeds a zone from a mailing-address state, and until
 * the business profile offered one nothing else set it: on 2026-09-25 no
 * business on prod had a zone.
 *
 * Offered by region, not by offset. "Central" means the same thing through
 * daylight saving, and the api stores the IANA identifier.
 */

export interface TimeZoneOption {
  value: string;
  label: string;
}

// The api's own list (Businesses::TimeZoneResolver::STATE_ZONES), so every
// choice here is one it already seeds from an address and validates.
export const US_TIME_ZONES: TimeZoneOption[] = [
  { value: 'America/New_York', label: 'Eastern (New York)' },
  { value: 'America/Chicago', label: 'Central (Chicago)' },
  { value: 'America/Denver', label: 'Mountain (Denver)' },
  { value: 'America/Phoenix', label: 'Mountain, no daylight saving (Phoenix)' },
  { value: 'America/Los_Angeles', label: 'Pacific (Los Angeles)' },
  { value: 'America/Anchorage', label: 'Alaska (Anchorage)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii (Honolulu)' },
  { value: 'America/Puerto_Rico', label: 'Atlantic (Puerto Rico)' },
  { value: 'America/St_Thomas', label: 'Atlantic (U.S. Virgin Islands)' },
  { value: 'Pacific/Guam', label: 'Chamorro (Guam)' },
  { value: 'Pacific/Saipan', label: 'Chamorro (Northern Mariana Islands)' },
  { value: 'Pacific/Pago_Pago', label: 'Samoa (American Samoa)' },
];

// Every other US zone a browser reports. That includes the older names CLDR
// still treats as canonical, America/Indianapolis and America/Louisville, which
// is what Chrome returns there. All are identifiers the api's TZInfo accepts.
const OTHER_US_ZONES = new Set([
  'America/Detroit', 'America/Kentucky/Louisville', 'America/Louisville',
  'America/Kentucky/Monticello', 'America/Indiana/Indianapolis', 'America/Indianapolis',
  'America/Indiana/Vincennes', 'America/Indiana/Winamac', 'America/Indiana/Marengo',
  'America/Indiana/Petersburg', 'America/Indiana/Vevay', 'America/Indiana/Tell_City',
  'America/Indiana/Knox', 'America/Menominee', 'America/North_Dakota/Center',
  'America/North_Dakota/New_Salem', 'America/North_Dakota/Beulah', 'America/Boise',
  'America/Juneau', 'America/Sitka', 'America/Metlakatla', 'America/Yakutat',
  'America/Nome', 'America/Adak',
]);

const isUsZone = (zone: string): boolean =>
  US_TIME_ZONES.some(option => option.value === zone) || OTHER_US_ZONES.has(zone);

/** The zone this browser is in, or null when it cannot say. */
export function browserTimeZone(): string | null {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch {
    return null;
  }
}

/**
 * The zone to give a business created in this browser, if any.
 *
 * The person creating a business is its owner, so theirs is the likeliest zone.
 * But only a US zone is sent: a machine set to UTC is more often a misconfigured
 * laptop than a business on UTC, and an identifier the api did not recognise
 * would fail the create itself. Anything else is left unset, and the business
 * profile asks.
 */
export function suggestedTimeZone(zone: string | null = browserTimeZone()): string | undefined {
  return zone && isUsZone(zone) ? zone : undefined;
}

/** How a zone reads: its name in the list, else its IANA identifier. */
export function timeZoneLabel(zone: string): string {
  return US_TIME_ZONES.find(option => option.value === zone)?.label ?? zone.replace(/_/g, ' ');
}

/**
 * The choices for a business: the US list, plus its current zone and this
 * browser's when either is missing from it. A Detroit browser reports
 * America/Detroit, not New York, and an owner there should see it offered.
 */
export function timeZoneOptions(
  current: string | null | undefined,
  browser: string | null = browserTimeZone()
): TimeZoneOption[] {
  const options = [...US_TIME_ZONES];
  [current, browser].forEach(zone => {
    if (zone && !options.some(option => option.value === zone)) {
      options.push({ value: zone, label: timeZoneLabel(zone) });
    }
  });
  return options;
}
